import { UseQueryOptions, UseQueryResult, useQuery } from 'react-query';
import {
  Connection as StardogConnection,
  query as StardogQuery,
} from 'stardog';

import {
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_DOMAIN_IRI,
  RDFS_RANGE_IRI,
  RDFS_SUBCLASS_IRI,
  STARDOG_LABEL_IRI,
} from 'bones/constants/iris';
import { MimeType } from 'bones/constants/mimeTypes';
import { queryBuilderColors } from 'bones/styles/colors';
import { arrayify } from 'bones/utils/arrayify';
import { ColorManager } from 'bones/utils/ColorManager';
import { getLocalName } from 'bones/utils/getLocalName';
import { formatJsonLd } from 'bones/utils/jsonld/formatJsonLdResults';
import { JsonLdResult } from 'bones/utils/jsonld/types';
import { runWithRefetchConnectionToken } from 'bones/utils/portal/refetchConnectionToken';
import { useStardogConnectionFromPortalIndex } from 'bones/utils/portal/useStardogConnectionFromPortalIndex';
import {
  QueryBuilderClass,
  QueryBuilderModel,
  QueryBuilderProperty,
} from 'bones/utils/queryBuilder/model';
import { alphaSort } from 'bones/utils/sort/alphaSort';
import { getTreeData } from 'bones/utils/tree/getTreeData';

const DESIGNER_COLOR_ID_IRI = 'tag:stardog:designer:colorId';

const VOICEBOX_MODEL_QUERY = `\
# Voicebox Model
CONSTRUCT {
  ?subject <tag:stardog:api:label> ?subjectLabel .
  ?subject a ?type .
  ?subject <http://www.w3.org/2000/01/rdf-schema#subClassOf> ?parent .
  ?subject <tag:stardog:designer:colorId> ?color .

  ?datatype <tag:stardog:api:label> ?datatypeLabel .
  ?datatype a <http://www.w3.org/2002/07/owl#DatatypeProperty> .
  ?datatype <http://www.w3.org/2000/01/rdf-schema#domain> ?subject .
  ?datatype <http://www.w3.org/2000/01/rdf-schema#range> ?datatypeRange .

  ?object <tag:stardog:api:label> ?objectLabel .
  ?object a <http://www.w3.org/2002/07/owl#ObjectProperty> .
  ?object <http://www.w3.org/2000/01/rdf-schema#domain> ?subject .
  ?object <http://www.w3.org/2000/01/rdf-schema#range> ?objectRange .
}
WHERE {
  {
    {
      ?subject a <http://www.w3.org/2002/07/owl#Class> .
    } UNION {
      ?subject a <http://www.w3.org/2004/02/skos/core#Concept> .
    } UNION {
      ?subject a <http://www.w3.org/2000/01/rdf-schema#Class> .
    }
    FILTER (!isBlank(?subject))
    ?subject a ?type .
    OPTIONAL {
      ?subject <tag:stardog:api:label> ?subjectLabel .
    }
    OPTIONAL {
      ?subject <http://www.w3.org/2000/01/rdf-schema#subClassOf> ?parent .
    }
    OPTIONAL {
      ?subject <tag:stardog:designer:colorId> ?color .
    }

    OPTIONAL {
      ?datatype a <http://www.w3.org/2002/07/owl#DatatypeProperty> .
      ?datatype <http://www.w3.org/2000/01/rdf-schema#domain>|<https://schema.org/domainIncludes>|<http://schema.org/domainIncludes> ?subject .
      OPTIONAL {
        ?datatype <tag:stardog:api:label> ?datatypeLabel .
      }
      OPTIONAL {
        ?datatype <http://www.w3.org/2000/01/rdf-schema#range>|<https://schema.org/rangeIncludes>|<http://schema.org/rangeIncludes> ?datatypeRange .
      }
    }

    OPTIONAL {
      ?object a <http://www.w3.org/2002/07/owl#ObjectProperty> .
      ?object <http://www.w3.org/2000/01/rdf-schema#domain>|<https://schema.org/domainIncludes>|<http://schema.org/domainIncludes> ?subject .
      OPTIONAL {
        ?object <tag:stardog:api:label> ?objectLabel .
      }
      OPTIONAL {
        ?object <http://www.w3.org/2000/01/rdf-schema#range>|<https://schema.org/rangeIncludes>|<http://schema.org/rangeIncludes> ?objectRange .
        FILTER EXISTS { #pragma reasoning off
          { ?objectRange a <http://www.w3.org/2002/07/owl#Class> } UNION
          { ?objectRange a <http://www.w3.org/2004/02/skos/core#Concept> } UNION
          { ?objectRange a <http://www.w3.org/2000/01/rdf-schema#Class> }
        }
      }
    }
  } UNION {
    ?datatype a <http://www.w3.org/2002/07/owl#DatatypeProperty> .
    OPTIONAL {
      ?datatype <tag:stardog:api:label> ?datatypeLabel .
    }
    OPTIONAL {
      ?datatype <http://www.w3.org/2000/01/rdf-schema#range>|<https://schema.org/rangeIncludes>|<http://schema.org/rangeIncludes> ?datatypeRange .
    }
  } UNION {
    ?object a <http://www.w3.org/2002/07/owl#ObjectProperty> .
    OPTIONAL {
      ?object <tag:stardog:api:label> ?objectLabel .
    }
    OPTIONAL {
      ?object <http://www.w3.org/2000/01/rdf-schema#range>|<https://schema.org/rangeIncludes>|<http://schema.org/rangeIncludes> ?objectRange .
      FILTER EXISTS { #pragma reasoning off
        { ?objectRange a <http://www.w3.org/2002/07/owl#Class> } UNION
        { ?objectRange a <http://www.w3.org/2004/02/skos/core#Concept> } UNION
        { ?objectRange a <http://www.w3.org/2000/01/rdf-schema#Class> }
      }
    }
  }
}
`;

const getIdsFromPredicateObject = (
  predicateObject: JsonLdResult[keyof JsonLdResult]
): string[] =>
  arrayify(predicateObject)
    .filter(
      (item): item is JsonLdResult & { '@id': string } =>
        typeof item === 'object' && typeof item['@id'] === 'string'
    )
    .map((item) => item['@id']);

const getJsonLdResultParents = (result: JsonLdResult) => {
  const parents = new Set<string>();
  const subClassOf = result[RDFS_SUBCLASS_IRI];
  if (typeof subClassOf === 'object') {
    const classResults: JsonLdResult[] = arrayify<string | JsonLdResult>(
      subClassOf
    ).filter(
      (classResult): classResult is JsonLdResult =>
        typeof classResult === 'object'
    );

    classResults.forEach((classResult) => {
      const iri = classResult['@id'];
      if (iri && typeof iri === 'string') {
        parents.add(iri);
      }
    });
  }

  return parents;
};

const getJsonLdStardogLabel = (result: JsonLdResult) => {
  const labels = result[STARDOG_LABEL_IRI];
  if (labels && typeof labels === 'object') {
    const labelResults: JsonLdResult[] = arrayify<string | JsonLdResult>(
      labels
    ).filter((label): label is JsonLdResult => typeof label === 'object');

    return labelResults.reduce<string>((acc, labelResult) => {
      if (acc) {
        return acc;
      }

      const labelValue = labelResult['@value'];
      if (labelValue && typeof labelValue === 'string') {
        return labelValue;
      }

      return acc;
    }, '');
  }
  return '';
};

const parseModelResults = (results: JsonLdResult[]) => {
  const propertiesByIri: Record<string, QueryBuilderProperty> = {};
  const classesByIri: Record<string, QueryBuilderClass> = {};
  const parentsByClassIri: Record<string, Set<string>> = {};
  const propertiesByDomain: Record<string, Set<string>> = {};
  const propertiesByRange: Record<string, Set<string>> = {};

  results.forEach((result) => {
    const iri = result['@id'];
    // istanbul ignore if
    if (typeof iri !== 'string') {
      return;
    }

    const types = arrayify(result['@type'] || /* istanbul ignore next */ []);
    const isDatatype = types.includes(OWL_DATATYPE_PROPERTY_IRI);
    const rawDesignerColorId = arrayify(
      result[DESIGNER_COLOR_ID_IRI] || []
    )[0] as JsonLdResult;
    const designerColorId = rawDesignerColorId
      ? (rawDesignerColorId['@value'] as string)
      : '';

    if (isDatatype || types.includes(OWL_OBJECT_PROPERTY_IRI)) {
      const domains = getIdsFromPredicateObject(
        result[RDFS_DOMAIN_IRI] || /* istanbul ignore next */ []
      );
      const ranges = getIdsFromPredicateObject(
        result[RDFS_RANGE_IRI] || /* istanbul ignore next */ []
      );
      domains.forEach((domain) => {
        if (!propertiesByDomain[domain]) {
          propertiesByDomain[domain] = new Set();
        }
        propertiesByDomain[domain].add(iri);
      });
      ranges.forEach((range) => {
        if (!propertiesByRange[range]) {
          propertiesByRange[range] = new Set();
        }
        propertiesByRange[range].add(iri);
      });
      propertiesByIri[iri] = {
        iri,
        label: getJsonLdStardogLabel(result) || getLocalName(iri),
        isDatatype,
        domains: new Set(domains),
        ranges: new Set(ranges),
      };
    } else {
      const parents = getJsonLdResultParents(result);
      parentsByClassIri[iri] = parents;
      classesByIri[iri] = {
        iri,
        label: getJsonLdStardogLabel(result) || getLocalName(iri),
        color: '',
        colorId: '',
        designerColorId,
        ancestors: new Set(),
        descendants: new Set(),
        incomingProperties: new Set(),
        outgoingProperties: new Set(),
      };
    }
  });

  const { tree } = getTreeData(
    Object.keys(classesByIri).map((iri) => ({
      id: iri,
      parents: parentsByClassIri[iri],
    }))
  );

  Object.keys(classesByIri).forEach((iri) => {
    const { ancestors, descendants } = tree[iri];
    classesByIri[iri].ancestors = ancestors;
    classesByIri[iri].descendants = descendants;

    const incomingProperties = new Set(propertiesByRange[iri]);
    const outgoingProperties = new Set(propertiesByDomain[iri]);

    // all ancestor properties also apply to this class
    ancestors.forEach((ancestor) => {
      propertiesByRange[ancestor]?.forEach((propertyIri) => {
        incomingProperties.add(propertyIri);
        propertiesByIri[propertyIri].ranges.add(iri);
      });
      propertiesByDomain[ancestor]?.forEach((propertyIri) => {
        outgoingProperties.add(propertyIri);
        propertiesByIri[propertyIri].domains.add(iri);
      });
    });

    classesByIri[iri].incomingProperties = incomingProperties;
    classesByIri[iri].outgoingProperties = outgoingProperties;
  });

  const colorManager = new ColorManager();
  Object.keys(classesByIri)
    .sort(alphaSort((iri) => classesByIri[iri].label))
    .forEach((iri) => {
      if (classesByIri[iri].designerColorId) {
        const colorToUse = queryBuilderColors.find(
          (color) => color.id === classesByIri[iri].designerColorId
        );
        if (colorToUse) {
          colorManager.claimColor(colorToUse, iri);
        }
      }

      const { id: colorId, color } = colorManager.getColorForIri(iri);
      classesByIri[iri].color = color;
      classesByIri[iri].colorId = colorId;
    });

  return { classesByIri, propertiesByIri };
};

const requestPortalVoiceboxModel =
  (
    stardogConnection: StardogConnection | null,
    connectionIdx: number,
    databaseId: string,
    model: string,
    modelGraphs?: string[]
  ) =>
  async () => {
    if (!stardogConnection || !databaseId || !model || !modelGraphs?.length) {
      return null;
    }

    const response = await runWithRefetchConnectionToken(
      stardogConnection,
      connectionIdx,
      (stardogConn) =>
        StardogQuery.execute(
          stardogConn,
          databaseId,
          VOICEBOX_MODEL_QUERY,
          MimeType.JSON_LD,
          { 'default-graph-uri': modelGraphs }
        )
    );

    if (!response.ok) {
      throw new Error(response.statusText || String(response.status));
    }

    const rawResults = await formatJsonLd(response.body);

    return { databaseId, model, results: parseModelResults(rawResults) };
  };

export const usePortalVoiceboxDatabaseModel = (
  connectionIdx: number,
  databaseId: string,
  model: string,
  modelGraphs?: string[],
  options?: UseQueryOptions<{
    databaseId: string;
    model: string;
    results: QueryBuilderModel;
  } | null>
): UseQueryResult<{
  databaseId: string;
  model: string;
  results: QueryBuilderModel;
} | null> => {
  const stardogConnection = useStardogConnectionFromPortalIndex(connectionIdx);
  return useQuery<{
    databaseId: string;
    model: string;
    results: QueryBuilderModel;
  } | null>(
    `usePortalVoiceboxModel-${
      stardogConnection ? connectionIdx : null
    }-${databaseId}-${model && modelGraphs?.length ? model : null}`,
    requestPortalVoiceboxModel(
      stardogConnection,
      connectionIdx,
      databaseId,
      model,
      modelGraphs
    ),
    options
  );
};
